import { Plural, Trans } from '@lingui/react/macro'

import type { ListSubscribersResponse_Subscriber } from '@jotta/grpc-connect-openapi/album'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@jotta/ui/AlertDialog'
import SvgUser from '@jotta/ui/SvgUser'

import { SubscribersAvatars } from '../AlbumInfo/AlbumMembers'

export interface UnShareAlbumDialogProps {
  open?: boolean
  albumTitle?: string
  subscribers?: ListSubscribersResponse_Subscriber[]
  onClose: () => void
  onUnShareAlbum: () => void
}

export const UnShareAlbumDialog: React.FC<UnShareAlbumDialogProps> = ({
  open = false,
  albumTitle,
  subscribers = [],
  onClose,
  onUnShareAlbum,
}) => {
  const subscriberCount = subscribers.length
  const hasImpact = subscriberCount > 0

  return (
    <AlertDialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose()
        }
      }}
    >
      <AlertDialogContent className="sm:max-w-[420px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {albumTitle ? (
              <Trans>Stop sharing "{albumTitle}"?</Trans>
            ) : (
              <Trans>Stop sharing this album?</Trans>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2">
              <p className="body-sm-ln text-on-surface-variant">
                <Trans>
                  The album becomes private to you again. This can't be undone.
                </Trans>
              </p>

              {hasImpact && (
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 rounded-2xl bg-surface-container-low px-3">
                  <div className="flex items-center py-3">
                    <SubscribersAvatars subscribers={subscribers} />
                  </div>
                  <p className="body-sm-ln text-on-surface">
                    <strong>
                      {subscriberCount}{' '}
                      <Plural
                        value={subscriberCount}
                        one="member"
                        other="members"
                      />
                    </strong>{' '}
                    <Plural
                      value={subscriberCount}
                      one="loses access, and the photos they added are removed"
                      other="lose access, and the photos they added are removed"
                    />
                  </p>

                  <div className="col-span-2 border-t border-light" />

                  <div className="flex items-center justify-center py-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-on-surface-variant">
                      <SvgUser width={14} />
                    </div>
                  </div>
                  <p className="body-sm-ln text-on-surface">
                    <strong>
                      <Trans>Your own photos</Trans>
                    </strong>{' '}
                    <Trans>stay exactly as they are</Trans>
                  </p>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            className="btn-destructive"
            onClick={onUnShareAlbum}
          >
            <Trans>Stop sharing</Trans>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
